<template>
  <div class="ps-tree">
    <div
      v-for="(layer, index) in layers"
      :key="layer.id || index"
      class="ps-tree-item"
    >
      <div
        class="ps-tree-row"
        :class="{ selected: selectedComponent && selectedComponent.id === layer.id }"
        @mouseenter="activeComponent(layer)"
        @mouseleave="disableAllActiveComponents()"
        :id="`component-layer-${layer.id}`"
      >
        <div class="ps-tree-main">
          <button
            v-if="hasChildren(layer)"
            type="button"
            class="ps-tree-toggle"
            @click.stop="toggleExpand(layer.id)"
          >
            <i class="bi" :class="isExpanded(layer.id) ? 'bi-chevron-down' : 'bi-chevron-left'"></i>
          </button>
          <span v-else class="ps-tree-toggle-spacer"></span>

          <i class="bi bi-box ps-tree-type-icon"></i>

          <span class="ps-tree-name" @click="setSelectedComponent(layer.id, 'default')">
            {{ layer.name }}
            <span v-if="componentChildCounter(layer) > 0" class="ps-tree-count">
              ({{ componentChildCounter(layer) }})
            </span>
          </span>
        </div>

        <div class="ps-tree-actions">
          <div class="ps-tree-actions-inner">
          <i
            class="bi bi-gear"
            title="تنظیمات / پراپ"
            @click.stop="setSelectedComponent(layer.id, 'default')"
          ></i>
          <i
            class="bi bi-plus-lg"
            title="افزودن کامپوننت"
            @click.stop="selectLayerForInsert(layer.id, 'default')"
          ></i>
          <i
            class="bi bi-robot"
            title="هوش مصنوعی"
            data-toggle="modal"
            :data-target="`#ai-modal-component-${layer.id}`"
          ></i>
          <i class="bi bi-back" title="کپی" @click.stop="copyComponentJson(layer)"></i>
          <i class="bi bi-scissors" title="برش (Cut)" @click.stop="cutLayer(layer)"></i>
          <i
            class="bi bi-clipboard-check"
            title="جای‌گذاری (Paste)"
            @click.stop="pastComponentJson({ currentLayer: layer, key: 'default' })"
          ></i>
          <i class="bi bi-trash" title="حذف" @click.stop="removeComponent(layer)"></i>
          <i class="bi bi-arrow-up-circle" title="بالا" @click.stop="moveComponentPosition(layer, 'up')"></i>
          <i class="bi bi-arrow-down-circle" title="پایین" @click.stop="moveComponentPosition(layer, 'down')"></i>
          </div>
        </div>
      </div>

      <teleport to="body">
        <div
          class="modal fade"
          :id="`ai-modal-component-${layer.id}`"
          tabindex="-1"
          aria-hidden="true"
        >
          <div class="modal-dialog">
            <div class="modal-content">
              <div class="modal-header">
                <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div class="modal-body">
                <ai-component :layer="layer"/>
              </div>
            </div>
          </div>
        </div>
      </teleport>

      <div class="ps-tree-children">
        <template v-for="(content, key) in layer.slots" :key="`${layer.id}-${key}`">
          <div class="ps-slot-bar">
            <span class="ps-slot-name">{{ key }}</span>
            <div class="ps-slot-actions">
              <i
                class="bi bi-clipboard-check-fill"
                title="جای‌گذاری"
                @click="pastComponentJson({ currentLayer: layer, key: key })"
              ></i>
              <i
                class="bi bi-input-cursor"
                title="پاک کردن متن"
                @click="clearComponentText(layer.id, key)"
              ></i>
              <i
                class="bi bi-plus-lg"
                title="افزودن کامپوننت"
                @click="selectLayerForInsert(layer.id, key)"
              ></i>
            </div>
          </div>
          <component-layer-manager
            v-if="isExpanded(layer.id) && content.components && content.components.length"
            :layers="content.components"
            :depth="depth + 1"
          />
        </template>
      </div>

      <teleport to=".component-setting">
        <component-setting :component="layer" v-if="selectedComponent && selectedComponent.id == layer.id"/>
      </teleport>
    </div>
  </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import ComponentSetting from "@/components/ComponentSetting";
import {toast} from "@moeinmdn/mixitweb-front-modules/src/plugin/class/Notification";
import AiComponent from "@/components/AiComponent.vue";

export default {
  name: "ComponentLayerManager",
  components: {AiComponent, ComponentSetting},
  props: {
    layers: {},
    depth: {type: Number, default: 0},
  },
  data() {
    return {
      expanded: {},
    }
  },
  computed: {
    ...mapGetters({
      profile: "User/getUserId",
      selectedComponent: "Site/getSelectedComponent"
    })
  },
  watch: {
    layers: {
      immediate: true,
      handler(list) {
        if (!Array.isArray(list)) return
        list.forEach(layer => {
          if (this.expanded[layer.id] === undefined) {
            this.expanded[layer.id] = this.depth < 2
          }
        })
      }
    }
  },
  methods: {
    ...mapActions({
      copyComponentJson: 'Site/copyComponentJson',
      pastComponentJson: 'Site/pastComponentJson',
      cutComponentJson: 'Site/cutComponentJson',
    }),
    hasChildren(layer) {
      if (!layer.slots) return false
      return Object.keys(layer.slots).some(key => {
        const comps = layer.slots[key]?.components
        return comps && comps.length > 0
      })
    },
    componentChildCounter(layer) {
      return Object.keys(layer.slots || {}).length
    },
    isExpanded(id) {
      return !!this.expanded[id]
    },
    toggleExpand(id) {
      this.expanded[id] = !this.expanded[id]
    },
    removeComponent(layer) {
      if (confirm('از حذف کامپوننت اطمینان دارید؟')) {
        this.$store.dispatch('Site/removeComponent', layer.id)
      }
    },
    async cutLayer(layer) {
      if (!confirm('این لایه برش داده شود؟')) return
      await this.cutComponentJson(layer)
    },
    async moveComponentPosition(layer, mode) {
      await this.$store.dispatch('Site/moveComponentPosition', {
        componentId: layer.id,
        mode: mode
      })
    },
    clearComponentText(id, slot) {
      this.$store.dispatch('Site/clearComponentText', {id, slot})
    },
    selectTarget(id, slot) {
      this.$store.dispatch('Site/setSelectedComponent', {id, slot})
    },
    selectLayerForInsert(id, slot) {
      this.selectTarget(id, slot)
      window.dispatchEvent(new CustomEvent('ps-open-components-modal'))
    },
    setSelectedComponent(id, slot) {
      this.selectTarget(id, slot)
      window.dispatchEvent(new CustomEvent('ps-open-settings'))
    },
    disableAllActiveComponents() {
      const elements = document.querySelectorAll(`.active-selected-component`)
      elements.forEach(item => {
        item.classList.remove('active-selected-component')
      })
    },
    activeComponent(layer) {
      const element = document.querySelector(`[data-selector='component-${layer.id}']`)
      if (element) {
        element.classList.toggle('active-selected-component')
      }
    },
  }
}
</script>

<style scoped>
.ps-tree {
  width: 100%;
}

.ps-tree-item {
  margin-top: 2px;
}

.ps-tree-row {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0;
  padding: 4px 6px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  color: #fbfbff;
  min-height: 28px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  transition: background-color 0.15s;
}

.ps-tree-main {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 4px;
  min-width: 0;
  user-select: none;
}

.ps-tree-row:hover {
  background: #f4f4f5;
  color: #09090b;
}

.ps-tree-row:hover .ps-tree-actions,
.ps-tree-row.selected .ps-tree-actions {
  grid-template-rows: 1fr;
  opacity: 1;
  margin-top: 4px;
  pointer-events: auto;
}

.ps-tree-row:hover .ps-tree-actions-inner {
  border-top-color: rgba(0, 0, 0, 0.12);
}

.ps-tree-row.selected {
  background: #4a4a4a;
  color: #fff;
}

.ps-tree-toggle {
  width: 18px;
  height: 18px;
  padding: 0;
  border: none;
  background: transparent;
  color: inherit;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  position: relative;
  z-index: 2;
}

.ps-tree-toggle-spacer {
  width: 18px;
  flex-shrink: 0;
}

.ps-tree-type-icon {
  font-size: 12px;
  opacity: 0.7;
  flex-shrink: 0;
}

.ps-tree-name {
  flex: 0 0 auto;
  white-space: nowrap;
}

.ps-tree-count {
  opacity: 0.65;
  font-size: 10px;
}

.ps-tree-actions {
  display: grid;
  grid-template-rows: 0fr;
  opacity: 0;
  margin-top: 0;
  pointer-events: none;
  transition: grid-template-rows 0.16s ease, opacity 0.12s ease, margin-top 0.16s ease;
}

.ps-tree-actions-inner {
  overflow: hidden;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  padding: 3px 2px 1px;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
}

.ps-tree-actions i {
  font-size: 12px;
  padding: 2px;
  cursor: pointer;
}

.ps-tree-actions i:hover {
  color: #a40303;
}

.ps-tree-children {
  margin-right: 12px;
  padding-right: 8px;
  border-right: 1px solid #555;
}

.ps-slot-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  padding: 2px 6px;
  margin: 2px 0;
  font-size: 10px;
  color: #27ae60;
  min-width: 0;
}

.ps-slot-name {
  flex: 0 1 auto;
  min-width: 0;
}

.ps-slot-actions {
  display: flex;
  flex-shrink: 0;
  gap: 4px;
}

.ps-slot-actions i {
  cursor: pointer;
  font-size: 12px;
}

.ps-slot-actions i:hover {
  color: #fff;
}
</style>
